const SharedResponsibilityModel = <template>
  {{! This file is auto-generated; see scripts/generate-compliance-html.sh for instructions. Do not edit directly. }}

  {{! template-lint-disable no-bare-strings }}
  <h1 id="hashicorpcloudplatformrolesresponsibilities">HashiCorp Cloud Platform
    Roles/Responsibilities</h1>
  <p><em>Last Updated: March 2025</em></p>
  <p>Security of the HashiCorp Cloud Platform (HCP) is a shared responsibility
    between HashiCorp, the customer, and the public cloud provider. This shared
    model can help reduce the customer's operational burden, as HashiCorp
    manages and controls certain components of the system, such as management of
    the operating system (e.g., updates and security patches), while the
    customer assumes the responsibilities for and management of access
    management, multifactor authentication (MFA), and configuration of access
    control lists (ACLs). As HCP is run entirely out of the public cloud, there
    are certain components that the cloud service providers are responsible for,
    such as the physical security of the facilities in which the service
    operates. Customers should carefully review the model outlined below to
    better understand the specific responsibilities that are under the
    customer's control.</p>
  <h2 id="thesharedresponsibilitymodel">The Shared-Responsibility Model</h2>
  <p>It is important to distinguish between the
    <em>security of the cloud</em>
    versus
    <em>security in the cloud</em>. The former is the responsibility of
    HashiCorp, while the latter is the responsibility of the customer.</p>
  <p>Additionally, there are
    <em>inherited security controls</em>
    from the public cloud provider. In this case, the public cloud provider
    manages the
    <em>physical and environmental controls</em>, meaning they are responsible
    for anything related to the physical data center and relevant controls.</p>
  <p>As for the security of the cloud, this responsibility lies with HashiCorp
    and includes controls related to high availability, vulnerability and patch
    management, upgrade cycles, backups, authentication, and authorization
    mechanisms.</p>
  <p>The customer, on the other hand, is responsible for the
    <em>security in the cloud</em>, meaning user access management, roles and
    permissions, configuration of ACLs, and proper data governance.</p>
  <p>Note: The term
    <em>application</em>
    referenced throughout this document refers to HashiCorp SaaS services or
    fully managed services.</p>
  <h2 id="sharedresponsibilitymodelforhcp">Shared Responsibility Model for HCP</h2>
  <table>
    <thead>
      <tr>
        <th>Stakeholder</th>
        <th>Responsible For</th>
        <th>Applicability</th>
        <th>Responsibility</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Customer</td>
        <td>Security in the application</td>
        <td>HCP Vault Radar
          <br />HCP Vault Secrets
          <br />HCP Terraform
          <br />HCP Packer
          <br />HCP Waypoint</td>
        <td>- Information and data transferred to/from HCP
          <br />- User access, accounts, and identities
          <br />- Roles and permissions</td>
      </tr>
      <tr>
        <td></td>
        <td></td>
        <td>HCP Consul Dedicated</td>
        <td>- Control of when to update to major and minor versions of HashiCorp
          services
          <br />- Consul service registry configuration to use service discovery
          and networking features
          <br />- Customer-specific network security configurations (e.g.,
          firewalls, Domain Name System [DNS] settings)</td>
      </tr>
      <tr>
        <td></td>
        <td></td>
        <td>HCP Boundary</td>
        <td>- Control of when to update to major versions of HashiCorp services,
          if chosen explicitly</td>
      </tr>
      <tr>
        <td></td>
        <td>Security and availability in the application</td>
        <td>HCP Vault Dedicated</td>
        <td>- Limited control of when to update to major versions of HashiCorp
          services
          <br />- Customer-specific network (HashiCorp Virtual Network (HVN))
          and security configurations (e.g., public vs. private access, Internet
          Protocol [IP] allow list, network peering, routing)
          <br />- Customer’s responsibility to set up a HVN associated with
          their standard or plus tier clusters, as backup HVN for opt-in
          Disaster Recovery capabilities</td>
      </tr>
      <tr>
        <td>HashiCorp</td>
        <td>Application security in the cloud</td>
        <td>All HCP products</td>
        <td>- Application management
          <br />- AuthN and AuthZ mechanisms
          <br />- Backups
          <br />- Operating system
          <br />- Virtual network controls (segmentation) and firewalls
          <br />- Encryption at rest and in transit
          <br />- Software upgrades, bugs, and fixes</td>
      </tr>
      <tr>
        <td>Public Cloud Provider</td>
        <td>Security of the cloud infrastructure</td>
        <td>All HCP products</td>
        <td>- Platform and supporting applications
          <br />- Compute resources
          <br />- Physical (hosts, network, data center)
          <br />- Physical networking
          <br />- Storage</td>
      </tr>
    </tbody>
  </table>
  <h2 id="hcpdatacollectionandstorage">HCP Data Collection and Storage</h2>
  <p>HCP, like any Software-as-a-Service (SaaS) provider, collects and stores
    data. This section breaks down what data is being collected, why it's being
    collected, and where it's stored.</p>
  <p>First, data is collected in the control plane, which is responsible for
    orchestrating the entire lifecycle of a cluster: creation, upgrades,
    snapshots, and, finally, destroying the cluster.</p>
  <p>Second, data is collected in the data plane, which is where our customers
    are able to customize the clusters, utilize the cluster, and request audit
    logs from HCP.</p>
  <p>Lastly, data is collected from customers that use HCP, such as IP
    addresses, name/email, and credit-card number.</p>
  <p>The tables below show what data is collected —and why — in the HCP control
    plane and data plane:</p>
  <p><strong>HCP Control Plane</strong></p>
  <table>
    <thead>
      <tr>
        <th>Data</th>
        <th>Description</th>
        <th>What is collected</th>
        <th>Why it's collected</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>User information</td>
        <td>From customer's use of the HCP portal or public APIs: Individuals or
          service accounts who establish an account with HashiCorp or otherwise
          use HashiCorp’s websites, products, and/or services</td>
        <td>- Name
          <br />- Email
          <br />- GitHub username (only if GitHub authentication is used to log
          into HCP)
          <br />- Credit-card number, billing address
          <br />- Credentials, domain names, and configurations to connect
          external identity providers with HCP organizations for single sign-on
          (SSO) authentication</td>
        <td>- Access HashiCorp products and websites, products, and/or services
          <br />- Billing
          <br />- Identity verification
          <br />- Two-factor authentication</td>
      </tr>
      <tr>
        <td>Users’ organization networking</td>
        <td>Networking and cloud provider or datacenter details needed to
          establish encrypted connections</td>
        <td>- Networking service identifiers related to the user organization's
          cloud provider
          <br />- Cloud-provider account ID for user organization
          <br />- User organization's cloud provider network subnets and routes</td>
        <td>Establishing an encrypted network connection from HCP to the user
          organization's cloud provider</td>
      </tr>
      <tr>
        <td>Audit logs</td>
        <td>Audit logs for HCP</td>
        <td>- Audit log messages for user and service-principal activity on the
          platform</td>
        <td>- Compliance, security, and audit purposes
          <br />- Product support</td>
      </tr>
      <tr>
        <td>Product telemetry</td>
        <td>Data gathered on the performance and use of the service and service
          components</td>
        <td>Measurements on:
          <br />- API performance times
          <br />- Application error information
          <br />- User feature-usage statistics and API calls
          <br />- User agent information
          <br />- IP addresses
          <br />- Page views and other clickstream data</td>
        <td>- Adherence to service-level objectives (SLOs) and service-level
          agreements (SLAs)
          <br />- Compliance, security, and audit purposes
          <br />- Product support
          <br />- Product performance</td>
      </tr>
    </tbody>
  </table>
  <p><strong>HCP Data Plane</strong></p>
  <table>
    <thead>
      <tr>
        <th>Data</th>
        <th>Description</th>
        <th>What is Collected</th>
        <th>Why It Is Collected</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Service logs</td>
        <td>Logs derived from HCP managed services</td>
        <td>Log messages</td>
        <td>- Adherence to SLOs and SLAs
          <br />- Compliance, security, and audit purposes
          <br />- Product support</td>
      </tr>
      <tr>
        <td>Snapshots</td>
        <td>Data snapshots/backups derived from HCP managed services</td>
        <td>Data snapshots</td>
        <td>- Compliance, security, and audit purposes
          <br />- Backup and recovery</td>
      </tr>
      <tr>
        <td>Product telemetry</td>
        <td>Data gathered on the performance and use of the service and service
          components</td>
        <td>Measurements on:
          <br />- API performance times
          <br />- Application error information
          <br />- User feature-usage statistics and API calls</td>
        <td>- Adherence to SLOs and SLAs
          <br />- Compliance, security, and audit purposes
          <br />- Product support
          <br />- Product performance</td>
      </tr>
    </tbody>
  </table>
  <h2 id="frequentlyaskedquestions">Frequently Asked Questions</h2>
  <p><strong>Which Public Cloud Provider regions are currently supported?</strong></p>
  <p>HCP Vault and Consul currently support these
    <a
      href="https://developer.hashicorp.com/hcp/docs/hcp/supported-env/aws"
    >regions</a>.</p>
  <p><strong>Where is the data stored in a HCP Vault cluster?</strong></p>
  <ul>
    <li><p>HCP Vault Customer Data is stored encrypted in the same region as the
        HCP Vault cluster.</p></li>
    <li><p>Cluster snapshot data is currently stored and encrypted in the US.</p></li>
    <li><p>Audit logs are stored and encrypted in the same region as the Vault
        cluster.</p></li>
    <li><p>Note: Audit logs are retained by HashiCorp for 12 months, then
        deleted.</p></li>
    <li><p>The company may also have access to other data, such as metadata and
        analytics data, that will be stored in the US.</p></li>
  </ul>
  <p><strong>Where is the data stored in a HCP Consul cluster?</strong></p>
  <ul>
    <li><p>HCP Consul customer data is stored encrypted in the same region as
        the HCP Consul cluster.</p></li>
    <li><p>Cluster snapshot data is currently stored and encrypted in the US.</p></li>
    <li><p>The company may also have access to other data, such as metadata and
        analytics data, that will be stored in the US.</p></li>
  </ul>
</template>;
export default SharedResponsibilityModel;
